import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { MdClose } from "react-icons/md";
import { useSwipeVertical } from "../hooks/useSwipe";

export default function LoginModal({
  isOpen,
  setIsOpen,
  onLogin,
  message,
  onDismiss,
}) {
  const handleLogin = () => {
    setIsOpen(false);
    onLogin?.();
  };

  const handleCancel = () => {
    setIsOpen(false);
    onDismiss?.(); // this updates localStorage cooldown
  };

  // Swipe down to close modal
  const { ref: modalRef } = useSwipeVertical({
    onSwipeDown: handleCancel,
    threshold: 80,
    enabled: isOpen,
  });

  return (
    <Dialog open={isOpen} onClose={handleCancel} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-md transition-opacity" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel 
          ref={modalRef}
          className="w-full max-w-md rounded-2xl bg-white shadow-2xl transform transition-all touch-pan-y"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-br from-orange-50 to-yellow-50 rounded-t-2xl p-6 border-b border-orange-100">
            <button
              onClick={handleCancel}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-600 hover:text-gray-900 transition-all duration-200 shadow-sm hover:shadow-md"
              aria-label="Close"
            >
              <MdClose className="text-lg" />
            </button>
            
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg">
                <ExclamationTriangleIcon className="h-7 w-7 text-white" />
              </div>
              <DialogTitle className="text-2xl font-bold text-gray-900">
                Login Required
              </DialogTitle>
            </div>
          </div>

          {/* Body */}
          <div className="p-6">
            <p className="text-gray-700 text-base leading-relaxed mb-6">
              {message || "You must be logged in to access the full features of the website. Please log in to continue."}
            </p>

            {/* Benefits of Logging In */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 mb-6 border border-blue-100">
              <p className="text-sm font-semibold text-gray-900 mb-2">Benefits of logging in:</p>
              <ul className="space-y-1.5 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold mt-0.5">✓</span>
                  <span>Access your order history and track deliveries</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold mt-0.5">✓</span>
                  <span>Earn cashback rewards on purchases</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold mt-0.5">✓</span>
                  <span>Manage your wallet and profile</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCancel}
                className="flex-1 px-5 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] text-sm md:text-base"
              >
                Cancel
              </button>
              <button
                onClick={handleLogin}
                className="flex-1 px-5 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-bold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] text-sm md:text-base"
              >
                Login Now
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
